import { inject, Injectable, Injector } from '@angular/core';
import { DateTime } from 'luxon';
import { BaseApiService } from './base-api.service';
import { CACHE_DURATIONS } from '../constants/cache.constants';
import { TIMEZONE, CACHE_KEYS } from '../constants/appointment.constants';
import { Appointment } from '../models/appointment.model';
import { OperationResult } from '../models/operation-result.model';
import { OperationResultService } from '../services/operation-result/operation-result.service';
import {
    AppointmentRow,
    WeeklyAppointmentRow,
    AppointmentPresenceSummaryRow,
} from '../models/supabase-dto.model';

type QrScanResult = {
    success: boolean;
    message: string;
    appointment_id: number | null;
    already_recorded: boolean;
};

@Injectable({ providedIn: 'root' })
export class AppointmentApiService extends BaseApiService {
    private readonly opResult = inject(OperationResultService);
    private readonly injector = inject(Injector);

    private async getActionQueueService() {
        const { ActionQueueService } = await import(
            '../services/action-queue/action-queue.service'
        );
        return this.injector.get(ActionQueueService);
    }

    // ========================================================================
    // READ OPERATIONS (cached)
    // ========================================================================

    async getAllAppointments(): Promise<AppointmentRow[]> {
        return this.fetchWithCache<AppointmentRow[]>({
            cacheKey: 'allAppointments',
            cacheTimestampKey: 'allAppointmentsTimestamp',
            rpcName: 'get_all_appointments',
            cacheDuration: CACHE_DURATIONS.SHORT,
        });
    }

    async getWeeklyAppointments(): Promise<WeeklyAppointmentRow[]> {
        return this.fetchWithCache<WeeklyAppointmentRow[]>({
            cacheKey: 'weeklyAppointments',
            cacheTimestampKey: 'weeklyAppointmentsTimestamp',
            rpcName: 'get_weekly_appointments',
            cacheDuration: CACHE_DURATIONS.MEDIUM,
        });
    }

    async getById(appointmentId: number): Promise<AppointmentRow | null> {
        const rows = await this.fetchWithCache<AppointmentRow[]>({
            cacheKey: `appointment:${appointmentId}`,
            cacheTimestampKey: `appointmentTimestamp:${appointmentId}`,
            rpcName: 'get_appointment_by_id',
            rpcParams: { _appointment_id: appointmentId },
            cacheDuration: CACHE_DURATIONS.VERY_SHORT,
        });

        if (!rows || rows.length === 0) {
            return null;
        }

        return rows[0];
    }

    async getPresenceSummary(
        appointmentId: string | null,
        cacheDuration?: number,
    ): Promise<AppointmentPresenceSummaryRow[]> {
        return this.fetchWithCache<AppointmentPresenceSummaryRow[]>({
            cacheKey: `appointmentPresenceSummary:${appointmentId}`,
            cacheTimestampKey: `appointmentPresenceSummaryTimestamp:${appointmentId}`,
            rpcName: 'get_appointment_presence_summary',
            rpcParams: { appointment_id: appointmentId },
            cacheDuration,
        });
    }

    // ========================================================================
    // QR OPERATIONS
    // ========================================================================

    async getQrToken(appointmentId: number): Promise<string> {
        if (!appointmentId) {
            throw new Error('Appointment ID requis pour generer le token QR.');
        }

        const cacheKey = `qrToken:${appointmentId}`;
        const cacheTimestampKey = `qrTokenTimestamp:${appointmentId}`;

        // Verifie le cache
        const cached = this.storage.getLocalStorageItem<string>(
            cacheKey,
            cacheTimestampKey,
            CACHE_DURATIONS.QR_TOKEN,
        );

        if (cached !== null && cached !== false) {
            return cached;
        }

        // Si hors ligne, essaie le cache expire
        if (!this.network.isOnline) {
            const expiredCache =
                this.storage.getLocalStorageItem<string>(cacheKey);
            if (expiredCache !== null && expiredCache !== false) {
                this.logger.info(
                    'Hors ligne: retour du token QR expire pour',
                    appointmentId,
                );
                return expiredCache as string;
            }
            throw new Error('Impossible de generer le QR code hors ligne.');
        }

        // Appel API
        const { data, error } = await this.supabase.rpc(
            'generate_appointment_qr_token',
            { _appointment_id: appointmentId },
        );

        if (error) {
            this.logger.error('Erreur generation token QR:', error);
            throw error;
        }

        // Mise en cache
        this.storage.setLocalStorage(cacheKey, data, true, cacheTimestampKey);

        return data as string;
    }

    async scanQrPresenceDirect(
        userId: string,
        qrToken: string,
        displayerAuthId: string | null = null,
    ): Promise<QrScanResult> {
        const rpcParams: Record<string, unknown> = {
            _user_auth_id: userId,
            _qr_token: qrToken,
        };
        if (displayerAuthId) {
            rpcParams['_displayer_auth_id'] = displayerAuthId;
        }

        const { data, error } = await this.supabase.rpc(
            'scan_qr_presence',
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            rpcParams as any,
        );

        if (error) {
            this.logger.error('Erreur scan QR:', error);
            throw error;
        }

        const row = Array.isArray(data) ? data[0] : data;

        if (!row || typeof row.success !== 'boolean') {
            this.logger.warn('Reponse scan QR inattendue:', data);
            return {
                success: false,
                message: 'Réponse invalide du serveur.',
                appointment_id: null,
                already_recorded: false,
            };
        }

        const result = row as QrScanResult;
        if (result.success) {
            this.invalidateCacheByPrefix(`memberStats${userId}`);
        }
        return result;
    }

    // ========================================================================
    // WRITE OPERATIONS (direct RPC, throw on error)
    // ========================================================================

    async insertDirect(appointment: Appointment): Promise<void> {
        const params = {
            _date: appointment.date,
            _start_time: appointment.startTime,
            _end_time: appointment.endTime,
            _location: appointment.location,
            _period: appointment.period ?? null,
            _appointments_name: appointment.name,
            _description: appointment.detail,
            _group: appointment.group?.id ?? null,
            _excuse_deadline: this.composeExcuseDeadline(
                appointment.excuseDeadlineDate,
                appointment.excuseDeadlineTime,
            ),
            _ignore_auto_excuses: appointment.ignoreAutoExcuses ?? false,
            _require_justification: appointment.requireJustification ?? false,
        };
        const { error } = await this.supabase.rpc(
            'create_appointment',
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            params as any,
        );

        if (error) throw error;
        this.invalidateAppointmentCaches();
    }

    async createJournalierDirect(appointment: Appointment): Promise<void> {
        const params = {
            _date: appointment.date,
            _location: appointment.location,
            _appointments_name: appointment.name,
            _description: appointment.detail,
            _group: appointment.group?.id ?? null,
            _excuse_deadline: this.composeExcuseDeadline(
                appointment.excuseDeadlineDate,
                appointment.excuseDeadlineTime,
            ),
            _ignore_auto_excuses: appointment.ignoreAutoExcuses ?? false,
            _require_justification: appointment.requireJustification ?? false,
        };
        const { error } = await this.supabase.rpc(
            'create_journalier_appointment',
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            params as any,
        );

        if (error) throw error;
        this.invalidateAppointmentCaches();
    }

    async update(
        id: number,
        data: {
            date: string;
            startTime: string | null;
            endTime: string | null;
            location: string;
            period: string | null;
            appointmentsName: string;
            description: string | null;
            group: number | null;
            excuseDeadline: string | null;
        },
    ): Promise<void> {
        const params = {
            _id: id,
            _date: data.date,
            _start_time: data.startTime,
            _end_time: data.endTime,
            _location: data.location,
            _period: data.period,
            _appointments_name: data.appointmentsName,
            _description: data.description,
            _group: data.group,
            _excuse_deadline: data.excuseDeadline,
        };
        const { error } = await this.supabase.rpc(
            'update_appointment',
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            params as any,
        );

        if (error) throw error;
        this.invalidateAppointmentCaches();
    }

    async convertToJournalier(id: number): Promise<void> {
        const { error } = await this.supabase.rpc(
            'convert_appointment_to_journalier',
            { _id: id },
        );

        if (error) throw error;
        this.invalidateAppointmentCaches();
    }

    async convertToTimed(
        id: number,
        startTime: string,
        endTime: string,
    ): Promise<void> {
        const { error } = await this.supabase.rpc(
            'convert_appointment_to_timed',
            {
                _id: id,
                _start_time: startTime,
                _end_time: endTime,
            },
        );

        if (error) throw error;
        this.invalidateAppointmentCaches();
    }

    async deleteCascade(id: number): Promise<void> {
        const { error } = await this.supabase.rpc(
            'delete_appointment_cascade',
            { _id: id },
        );

        if (error) throw error;
        this.invalidateAppointmentCaches();
    }

    // ========================================================================
    // OPERATION RESULT WRAPPERS
    // ========================================================================

    async insertAppointment(
        appointment: Appointment,
    ): Promise<OperationResult> {
        if (!this.network.isOnline)
            return this.opResult.error(
                'INSERT_APPOINTMENT',
                'Action non disponible hors ligne.',
            );
        try {
            await this.insertDirect(appointment);
            return this.opResult.success('INSERT_APPOINTMENT');
        } catch (error) {
            return this.opResult.error('INSERT_APPOINTMENT', error as Error);
        }
    }

    async createJournalierAppointment(
        appointment: Appointment,
    ): Promise<OperationResult> {
        if (!this.network.isOnline)
            return this.opResult.error(
                'CREATE_JOURNALIER_APPOINTMENT',
                'Action non disponible hors ligne.',
            );
        try {
            await this.createJournalierDirect(appointment);
            return this.opResult.success('CREATE_JOURNALIER_APPOINTMENT');
        } catch (error) {
            return this.opResult.error(
                'CREATE_JOURNALIER_APPOINTMENT',
                error as Error,
            );
        }
    }

    async updateAppointment(
        id: number,
        data: {
            date: string;
            startTime: string | null;
            endTime: string | null;
            location: string;
            period: string | null;
            appointmentsName: string;
            description: string | null;
            group: number | null;
            excuseDeadline: string | null;
        },
    ): Promise<OperationResult> {
        if (!this.network.isOnline)
            return this.opResult.error(
                'UPDATE_APPOINTMENT',
                'Action non disponible hors ligne.',
            );
        try {
            await this.update(id, data);
            return this.opResult.success('UPDATE_APPOINTMENT');
        } catch (error) {
            return this.opResult.error('UPDATE_APPOINTMENT', error as Error);
        }
    }

    async convertToJournalierOp(id: number): Promise<OperationResult> {
        if (!this.network.isOnline)
            return this.opResult.error(
                'CONVERT_TO_JOURNALIER',
                'Action non disponible hors ligne.',
            );
        try {
            await this.convertToJournalier(id);
            return this.opResult.success('CONVERT_TO_JOURNALIER');
        } catch (error) {
            return this.opResult.error('CONVERT_TO_JOURNALIER', error as Error);
        }
    }

    async convertToTimedOp(
        id: number,
        startTime: string,
        endTime: string,
    ): Promise<OperationResult> {
        if (!this.network.isOnline)
            return this.opResult.error(
                'CONVERT_TO_TIMED',
                'Action non disponible hors ligne.',
            );
        try {
            await this.convertToTimed(id, startTime, endTime);
            return this.opResult.success('CONVERT_TO_TIMED');
        } catch (error) {
            return this.opResult.error('CONVERT_TO_TIMED', error as Error);
        }
    }

    async deleteAppointmentCascade(id: number): Promise<OperationResult> {
        if (!this.network.isOnline)
            return this.opResult.error(
                'DELETE_APPOINTMENT',
                'Action non disponible hors ligne.',
            );
        try {
            await this.deleteCascade(id);
            return this.opResult.success('DELETE_APPOINTMENT');
        } catch (error) {
            return this.opResult.error('DELETE_APPOINTMENT', error as Error);
        }
    }

    async scanQrPresence(
        userId: string,
        qrToken: string,
        displayerAuthId: string | null = null,
    ): Promise<{
        success: boolean;
        message: string;
        appointment_id: number | null;
        already_recorded: boolean;
    }> {
        if (!this.network.isOnline) {
            const aq = await this.getActionQueueService();
            if (aq.hasQueuedQrScan(qrToken)) {
                return {
                    success: true,
                    message:
                        'Cette demande est déjà en attente de synchronisation.',
                    appointment_id: null,
                    already_recorded: true,
                };
            }
            aq.enqueue({
                type: 'SCAN_QR_PRESENCE',
                payload: { userId, qrToken, displayerAuthId },
            });
            return {
                success: true,
                message:
                    'Présence enregistrée hors ligne. Elle sera synchronisée automatiquement.',
                appointment_id: null,
                already_recorded: false,
            };
        }
        return this.scanQrPresenceDirect(userId, qrToken, displayerAuthId);
    }

    // ========================================================================
    // CACHE INVALIDATION
    // ========================================================================

    /**
     * Invalide toutes les entrees de cache liees aux appointments.
     *
     * Utilise des prefixes pour couvrir les caches par-id (detail + presence
     * summary) y compris ceux de RDV lies (journalier) dont l'id n'est pas
     * toujours connu du caller. Les collisions sont evitees par des keys
     * prefixes de facon distincte (`appointment:` vs `appointmentPresenceSummary:`).
     */
    invalidateAppointmentCaches(): void {
        this.invalidateCache(
            CACHE_KEYS.ALL_APPOINTMENTS,
            CACHE_KEYS.ALL_APPOINTMENTS_TIMESTAMP,
        );
        this.invalidateCache(
            CACHE_KEYS.WEEKLY_APPOINTMENTS,
            CACHE_KEYS.WEEKLY_APPOINTMENTS_TIMESTAMP,
        );
        this.invalidateCacheByPrefix('appointment:');
        this.invalidateCacheByPrefix('appointmentTimestamp:');
        this.invalidateCacheByPrefix('appointmentPresenceSummary:');
        this.invalidateCacheByPrefix('appointmentPresenceSummaryTimestamp:');
    }

    // ========================================================================
    // PRIVATE HELPERS
    // ========================================================================

    private composeExcuseDeadline(
        date: string | null,
        time: string | null,
    ): string | null {
        if (!date || !time) return null;
        const dt = DateTime.fromFormat(`${date} ${time}`, 'yyyy-MM-dd HH:mm', {
            zone: TIMEZONE,
        });
        return dt.toISO();
    }
}
